import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Constante } from '../../constante';
import { AuthSession, LoginGoogleRequest, LoginRequest, RegisterRequest } from '../models/api/auth-api';
import { ResponseApi } from '../models/response-api';
import { AppUtil } from '../utility/app-util';
import { AuthUtility } from '../utility/auth-utility';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private http = inject(HttpClient);
    private url = AppUtil.addTrailingSlash(environment.URL_API_PEDIDOS) + 'api/auth/';
    session = signal<AuthSession | null>(AuthUtility.getValueUser<AuthSession>());

    login(body: LoginRequest): Observable<AuthSession> {
        return this.http.post<ResponseApi<AuthSession>>(this.url + 'login', body).pipe(
            map((res) => this.requireData(res)),
            tap((data) => this.persist(data))
        );
    }

    register(body: RegisterRequest): Observable<AuthSession> {
        return this.http.post<ResponseApi<AuthSession>>(this.url + 'register', body).pipe(
            map((res) => this.requireData(res)),
            tap((data) => this.persist(data))
        );
    }

    loginGoogle(body: LoginGoogleRequest): Observable<AuthSession> {
        return this.http.post<ResponseApi<AuthSession>>(this.url + 'login-google', body).pipe(
            map((res) => this.requireData(res)),
            tap((data) => this.persist(data))
        );
    }

    logout(): void {
        if (AuthUtility.isAuthenticated()) {
            this.http.post<ResponseApi<unknown>>(this.url + 'logout', {}).subscribe({ error: () => undefined });
        }
        AuthUtility.closeSessionData();
        this.session.set(null);
    }

    isAdmin(): boolean {
        const roles = this.session()?.roles ?? [];
        return roles.some((r) => r === 'ADMIN_EMPRESA' || r === 'SUPERADMIN');
    }

    isAuthenticated(): boolean {
        return AuthUtility.isAuthenticated();
    }

    private persist(data: AuthSession): void {
        AuthUtility.initSessionData(data.token);
        AuthUtility.setValueUser(data);
        this.session.set(data);
    }

    private requireData(res: ResponseApi<AuthSession>): AuthSession {
        if (res.success !== Constante.REQUEST_STATUS_OK || !res.data?.token) {
            const errors = res.errors as { message?: string }[] | null;
            throw new Error(errors?.[0]?.message || res.message || 'No se pudo iniciar sesión.');
        }
        return res.data;
    }
}
